import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Autocomplete,
  MenuItem,
} from '@mui/material';
import { authFetch } from '../../../utils/authHelpers';
import { AI_ENGINE_URL } from '../../../utils/apiConfig';
import { leesJson, meldFout } from '../../../utils/apiFout';
import { T, Icon, stitchInputSx, stitchGradientBtn, stitchOutlineBtn } from '../parts/stitch';
import TaskItem from '../parts/TaskItem';
import PhaseTasksView from '../parts/PhaseTasksView';
import TimelineTasksView from '../parts/TimelineTasksView';
import GroupedTasksView from '../parts/GroupedTasksView';
import PhaseKanbanView from '../PhaseKanbanView';
import { useProjectData } from '../ProjectDataContext';

function TasksPanel({ tasksView, setTasksView, kanbanShowPLOnly, setKanbanShowPLOnly, onOpenSmartTasks, onOpenPreproWizard }) {
  const {
    tasks,
    deliverables,
    setSelectedTask,
    setAutoEditTaskDate,
    newTaskText,
    setNewTaskText,
    savingTask,
    handleAddQuickTask,
    handleTaskUpdate,
    handleToggleComplete,
  } = useProjectData();

  // Uitklapbare extra's bij snel toevoegen: uitvoerder, datum en fase/type
  // (Joris, 1 sep). Standaard dicht — de snelle flow blijft één Enter.
  const [meerOpen, setMeerOpen] = useState(false);
  const [opties, setOpties] = useState({ assigneeId: '', datum: '', type: '' });
  const [personen, setPersonen] = useState(null);
  useEffect(() => {
    if (!meerOpen || personen !== null) return;
    (async () => {
      try {
        const r = await authFetch(`${AI_ENGINE_URL}/api/v1/airtable/persons?status=Active`);
        const d = await leesJson(r, 'Personen laden');
        setPersonen((d.people || [])
          .filter((p) => p.naam && ['Medewerker', 'Stagiair'].includes(p.type || ''))
          .map((p) => ({ id: p.id, naam: p.naam })));
      } catch (e) { meldFout(e, 'Personen laden'); setPersonen([]); }
    })();
  }, [meerOpen, personen]);
  const TAAK_FASES = ['Preproductie', 'Script', 'Regie', 'Illustratie', 'Draaidag',
    'Montage', 'Animatie', 'Grading', 'Audio', 'Overig'];
  const voegToe = () => handleAddQuickTask({
    assigneeId: opties.assigneeId || undefined,
    datum: opties.datum || undefined,
    type: opties.type || undefined,
  });

  const taskViewOptions = [
    { value: 'kanban', icon: 'view_column', label: 'Kanban' },
    { value: 'phase', icon: 'event', label: 'Per Fase' },
    { value: 'timeline', icon: 'calendar_today', label: 'Timeline' },
    { value: 'grouped', icon: 'movie', label: 'Per Deliverable' },
    { value: 'list', icon: 'view_list', label: 'Alle taken' },
  ];

  return (
    <>
        {/* Quick add task input - always visible */}
        <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Snel taak toevoegen (Enter om op te slaan)..."
            value={newTaskText}
            onChange={(e) => setNewTaskText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                voegToe();
              }
            }}
            disabled={savingTask}
            sx={stitchInputSx}
          />
          <Box
            component="span"
            onClick={() => setMeerOpen((v) => !v)}
            title="Uitvoerder, datum en fase meegeven"
            sx={{
              ...stitchOutlineBtn,
              px: 1.25,
              py: 1,
              ...(meerOpen || opties.assigneeId || opties.datum || opties.type
                ? { borderColor: T.primary, color: T.primary } : {}),
            }}
          >
            <Icon name="tune" size={18} />
          </Box>
          <Box
            component="span"
            onClick={(!newTaskText.trim() || savingTask) ? undefined : voegToe}
            sx={{
              ...stitchGradientBtn,
              px: 1.5,
              py: 1,
              opacity: (!newTaskText.trim() || savingTask) ? 0.5 : 1,
              cursor: (!newTaskText.trim() || savingTask) ? 'default' : 'pointer',
            }}
          >
            {savingTask ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : <Icon name="add" size={20} />}
          </Box>
        </Box>
        {meerOpen && (
          <Box sx={{ mb: 2, mt: -1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Autocomplete
              size="small"
              sx={{ minWidth: 220 }}
              options={personen || []}
              loading={personen === null}
              value={(personen || []).find((p) => p.id === opties.assigneeId) || null}
              getOptionLabel={(o) => o?.naam || ''}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              onChange={(_e, v) => setOpties((prev) => ({ ...prev, assigneeId: v?.id || '' }))}
              renderInput={(params) => (
                <TextField {...params} placeholder="Uitvoerder (standaard: jijzelf)" sx={stitchInputSx} />
              )}
            />
            <TextField
              size="small"
              type="date"
              label="Datum"
              InputLabelProps={{ shrink: true }}
              value={opties.datum}
              onChange={(e) => setOpties((prev) => ({ ...prev, datum: e.target.value }))}
              sx={{ ...stitchInputSx, minWidth: 160 }}
            />
            <TextField
              size="small"
              select
              label="Fase"
              value={opties.type}
              onChange={(e) => setOpties((prev) => ({ ...prev, type: e.target.value }))}
              sx={{ ...stitchInputSx, minWidth: 160 }}
            >
              <MenuItem value="">Geen fase</MenuItem>
              {TAAK_FASES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
          </Box>
        )}

        {tasks.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Icon name="task_alt" size={48} sx={{ color: T.outlineVariant, display: 'block', mx: 'auto', mb: 1 }} />
            <Typography sx={{ fontFamily: T.fontBody, color: T.outline, mb: 2 }}>Geen taken gevonden</Typography>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
              <Box
                component="span"
                onClick={() => onOpenSmartTasks()}
                sx={stitchGradientBtn}
              >
                <Icon name="auto_awesome" size={18} />
                Slimme taken (offerte)
              </Box>
              <Box
                component="span"
                onClick={() => onOpenPreproWizard()}
                sx={stitchOutlineBtn}
              >
                <Icon name="playlist_add" size={18} />
                Projecttaken genereren
              </Box>
            </Box>
          </Box>
        ) : (
          <Box>
            {/* Header with View Toggle and New Task Button */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Box
                  component="span"
                  onClick={() => onOpenSmartTasks()}
                  sx={stitchGradientBtn}
                >
                  <Icon name="auto_awesome" size={16} />
                  Slimme taken (offerte)
                </Box>
                <Box
                  component="span"
                  onClick={() => onOpenPreproWizard()}
                  sx={stitchOutlineBtn}
                >
                  <Icon name="playlist_add" size={16} />
                  Projecttaken genereren
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5, p: 0.5, bgcolor: T.surfaceLow, borderRadius: '9999px' }}>
                {taskViewOptions.map(opt => (
                  <Box
                    key={opt.value}
                    onClick={() => setTasksView(opt.value)}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 0.5,
                      px: 1.5, py: 0.5,
                      borderRadius: '9999px',
                      fontFamily: T.fontBody,
                      fontSize: '0.75rem',
                      fontWeight: tasksView === opt.value ? 700 : 500,
                      color: tasksView === opt.value ? '#fff' : T.secondary,
                      bgcolor: tasksView === opt.value ? T.primary : 'transparent',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                      '&:hover': tasksView === opt.value ? {} : { bgcolor: T.surfaceHigh },
                    }}
                  >
                    <Icon name={opt.icon} size={16} />
                    {opt.label}
                  </Box>
                ))}
              </Box>
            </Box>

            {tasksView === 'kanban' ? (
              <Box>
                {/* PL Only Toggle */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={kanbanShowPLOnly}
                        onChange={(e) => setKanbanShowPLOnly(e.target.checked)}
                      />
                    }
                    label={<Typography variant="caption" sx={{ fontFamily: T.fontBody }}>Alleen PL taken</Typography>}
                  />
                </Box>
                <PhaseKanbanView
                  tasks={tasks
                    .filter(t => {
                      const status = t.fields?.Status || '';
                      return !status.toLowerCase().includes('gearchiveerd') && !status.toLowerCase().includes('archief');
                    })
                    .map(task => {
                      const type = task.fields?.Type || '';
                      const taskName = task.fields?.NaamTaak || 'Taak';
                      // Map task type to role and phase for Kanban
                      const typeToRole = {
                        'Preproductie': 'Projectleider',
                        'Draaidag': 'Regisseur',
                        'Montage': 'Editor',
                        'Animatie': 'Animator',
                        'Script': 'Copywriter',
                        'Regie': 'Regisseur',
                        'Grading': 'Editor',
                        'Audio': 'Editor',
                        'IT': 'Projectleider',
                        'Marketing': 'Accountmanager',
                        'Social Strategie': 'Accountmanager',
                        'Deadline': 'Projectleider',
                        'Afspraak': 'Projectleider',
                        'Projectafronding': 'Projectleider',
                      };
                      // Map to PhaseKanbanView phases using keywords that will match
                      const typeToPhaseKeyword = {
                        'Marketing': 'concept',
                        'Social Strategie': 'concept',
                        'Script': 'script',
                        'Preproductie': 'planning',
                        'Draaidag': 'draaidag',
                        'Montage': 'montage',
                        'Animatie': 'animatie',
                        'Regie': 'review',
                        'Grading': 'color',
                        'Audio': 'geluid',
                        'Projectafronding': 'oplevering',
                        'Deadline': 'deadline',
                      };
                      return {
                        id: task.id,
                        name: taskName,
                        role: typeToRole[type] || 'Projectleider',
                        phase_name: typeToPhaseKeyword[type] || '',
                        is_included: task.fields?.Afgerond !== true,
                        estimated_hours: task.fields?.GeschatteTijd || 0,
                        start_date: task.fields?.StartDateUitvoerder,
                        end_date: task.fields?.EndDateUitvoerder || task.fields?.Deadline,
                      };
                    })}
                  compact={true}
                  useAbbreviations={true}
                  roleFilter={kanbanShowPLOnly ? 'Projectleider' : null}
                  title="Taken per fase"
                  hideHeader={false}
                  onTaskToggle={(task) => {
                    const originalTask = tasks.find(t => t.id === task.id);
                    if (originalTask) setSelectedTask(originalTask);
                  }}
                />
              </Box>
            ) : tasksView === 'phase' ? (
              <PhaseTasksView tasks={tasks} onTaskClick={setSelectedTask} onToggleComplete={handleToggleComplete} />
            ) : tasksView === 'timeline' ? (
              <TimelineTasksView tasks={tasks} deliverables={deliverables} onTaskClick={(task) => { setSelectedTask(task); setAutoEditTaskDate(true); }} onTaskUpdate={handleTaskUpdate} />
            ) : tasksView === 'grouped' ? (
              <GroupedTasksView tasks={tasks} onTaskClick={setSelectedTask} onToggleComplete={handleToggleComplete} />
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {tasks.map((task) => <TaskItem key={task.id} task={task} onClick={() => setSelectedTask(task)} onToggleComplete={handleToggleComplete} />)}
              </Box>
            )}
          </Box>
        )}
    </>
  );
}

export default TasksPanel;
